/* eslint-disable @next/next/no-img-element */
"use client";

import Login from "@/app/_components/Login/Login";
import { User, UserRoles } from "@/models/user";
import { useAppSelector } from "@/redux/store";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import SignUpForm from "../Login/SignUpForm";
// import { onAuthStateChanged } from "@/helpers/firebase/auth";
import {
  AuthError,
  User as FirebaseUser,
  onAuthStateChanged,
  updateEmail,
} from "firebase/auth";
import {
  appInitialized,
  logIn,
  logOut,
  userDetails,
  setUserPasses,
} from "@/redux/features/app-slice";
import {
  createUser,
  getUserInfoById,
  getUserPasses,
} from "@/helpers/server-actions/users-collection";
import { useDispatch } from "react-redux";
import { nonProtectedRoutes } from "@/helpers/utils/constants";
import Loader from "../Loader";
import { getCookie } from "cookies-next";
import { auth } from "@/helpers/firebase/firebase";
import { identifyUser, resetUser } from "@/lib/posthog";
import Image from "next/image";
export default function LoginLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User>();
  const [authUser, setAuthUser] = useState<FirebaseUser | undefined>();
  const [isProfileCompleted, setIsProfileCompleted] = useState<boolean>(false);
  const [isBypassLogin, setIsBypassLogin] = useState<boolean>(false);
  const [isByPassProfileSetup, setIsByPassProfileSetup] =
    useState<boolean>(false);
  const userData = useAppSelector((state) => state.appReducer.userData);
  const isAppInit = useAppSelector((state) => state.appReducer.isAppInit);
  const pathname = usePathname();
  const dispatch = useDispatch();

  useEffect(() => {
    if (nonProtectedRoutes.find((route) => pathname.includes(route))) {
      setIsBypassLogin(true);
    } else {
      setIsBypassLogin(false);
    }
  }, [pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      handleOnAuthStateChanged(user);
    });
    return () => unsubscribe();
  }, []);

  const handleOnAuthStateChanged = async (
    authUserInner: FirebaseUser | null
  ) => {
    dispatch(appInitialized());

    if (authUserInner) {
      console.log("[LoginLayout] Auth user detected:", authUserInner.uid);
      setAuthUser(authUserInner);
      dispatch(logIn(authUserInner));
      identifyUser(authUserInner.uid, {
        ...(authUserInner.email ? { email: authUserInner.email } : {}),
      });
      if (!userData) {
        try {
          console.log("[LoginLayout] Creating user if not exists...");
          await createUserIfNotExists(authUserInner);
          console.log("[LoginLayout] Fetching user info...");
          await getUserInfo(authUserInner.uid);
          console.log("[LoginLayout] User info loaded successfully");
        } catch (error) {
          console.error("[LoginLayout] Error loading user data:", error);
          // Set a default user to unblock the UI
          dispatch(userDetails({
            uid: authUserInner.uid,
            email: authUserInner.email || "",
            role: UserRoles.user,
          } as User));
        }
      }
    } else {
      console.log("[LoginLayout] No auth user, logging out");
      setAuthUser(undefined);
      dispatch(logOut());
      resetUser();
    }
  };

  useEffect(() => {
    setUser(userData);
    if (userData) {
      if (pathname?.includes("/passes/buy")) {
        // bypass the explore intrest when buying passes
        setIsByPassProfileSetup(true);
      } else {
        setIsProfileCompleted(checkIfProfileCompleted(userData));
      }
    }
  }, [userData]);

  const checkIfProfileCompleted = (user: User) => {
    return (
      !!user.firstName && !!user.lastName && !!user.motivations && !!user.tags
    );
  };

  const createUserIfNotExists = async (authUser: FirebaseUser) => {
    if (authUser.uid) {
      let email = authUser.email ?? "";
      if (authUser.uid.startsWith("linkedin:")) {
        const idTokenResult = await authUser.getIdTokenResult();
        let linkedInEmail = (idTokenResult.claims["email"] ?? "") as string;
        if (!linkedInEmail) {
          const authInfo = getCookie("linkedin_auth_info");
          if (authInfo) {
            const json: { token: string; email: string } = JSON.parse(authInfo);
            linkedInEmail = json.email ?? "";
          }
        }
        try {
          if (!!linkedInEmail) {
            await updateEmail(authUser, linkedInEmail);
            email = linkedInEmail;
          }
        } catch (e) {
          const authError = e as AuthError;
          // do nothing
        }
      }
      const user = await getUserInfoById(authUser.uid);
      if (!user) {
        const userPayload: User = {
          email: email.toLowerCase(),
          authProvider: authUser.providerId,
          uid: authUser.uid,
          phoneNumber: authUser.phoneNumber ?? "",
          role: UserRoles.user,
          
        };
        await createUser(userPayload, authUser.uid);
      }
    }

    return null;
  };

  const getUserInfo = async (userId: string) => {
    try {
      const data = await Promise.race([
        getUserInfoById(userId),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout loading user data")), 10000)
        )
      ]) as User | null;

      if (data) {
        dispatch(userDetails(data));
        if (data.uid) {
          await getUserPassInfo(data.uid);
        }
      } else {
        console.warn("[LoginLayout] No user data found for:", userId);
      }
    } catch (error) {
      console.error("[LoginLayout] getUserInfo failed:", error);
      throw error;
    }
  };

  const getUserPassInfo = async (docId: string) => {
    const data = await getUserPasses(docId);
    if (data) {
      dispatch(setUserPasses(data));
    }
  };

  if (!isAppInit || (authUser && !user))
    return (
      <>
        <Loader />
      </>
    );
  else if (
    (user && (isProfileCompleted || isByPassProfileSetup)) ||
    isBypassLogin
  )
    return <>{children}</>;
  else
    return (
      <div className="loginLayout-background-container h-full">
        <Image
          className="block sm:hidden"
          fill
          src={"/images/mobile-background.svg"}
          alt="background"
          objectFit="cover"
        />
        <Image
          className="hidden sm:block"
          fill
          src={"/images/desktop-background.svg"}
          alt="background"
          objectFit="cover"
        />

        <div className="loginLayout-content">
          <img
            className="h-11 w-25 mt-20 absolute top-[-2.5rem] left-8"
            src="/images/C-Tribe_Main_Transparent_Logo.svg"
            alt="c-tribe"
          />
          {user && !isProfileCompleted ? <SignUpForm /> : <Login />}
        </div>
      </div>
    );
}
